import RequestHandler from './requestHandling';
import { snapshotForStorage } from './listViewDiff';
import { serializeYesplanAPIDate } from './dateTimeUtilities';
import { listViewConfigurationsIn } from './listViewSettings';
import type { StoredViewSettings } from './listViewSettings';
import type {
  CustomDataTreeNode,
  ListViewBooking,
  ListViewConfiguration,
  ListViewDate,
  ListViewSnapshot
} from '../listview/types';

/* Every request the Views widget makes, one function per endpoint, so no
 * component or hook has to spell out a path, a header or a response shape.
 *
 * The callback style is deliberate: RequestHandler rejects on 5xx, on a 440
 * and on a server version change, and the app relies on those rejections
 * staying unhandled so NetworkDisconnectionBoundary and
 * DetectServerUpdateBoundary can act on the window's unhandledrejection
 * event. Nothing in here may await, catch or otherwise settle the promises
 * RequestHandler returns. 4xx arrives as the callback's error argument
 * instead, which is the caller's business.
 *
 * A GET of a per-user document (/viewsettings, /viewsnapshot) answers 404
 * when the user has none yet, which is the normal first-visit case rather
 * than a failure: callers must treat "the request came back" as the signal to
 * carry on, not "a document arrived".
 *
 * Every decision worth testing is deliberately somewhere else — which
 * dashboard is renderable in listViewSettings, how a snapshot is trimmed to
 * fit in listViewDiff — because this module cannot be loaded outside a bundler
 * at all: RequestHandler pulls in Sentry and superagent, which forces Node's
 * ESM loader and so cannot be imported from a mocha suite. What is left here
 * is paths, headers and response shapes; the endpoints themselves are covered
 * by the server route tests. */

type RequestError = { code: number } | null;

type ResultCallback<T> = (body: T | null, error: RequestError) => void;

/* Adapts RequestHandler's (response, error) callback to (body, error): every
   caller here wants the payload, never the superagent response */
function withBody<T>(callback: ResultCallback<T>) {
  return (result: { body: T }, error: RequestError) =>
    callback(error ? null : result.body, error);
}

const ignoreResult = () => {};

export function fetchListViewConfigurations(
  host: string,
  callback: ResultCallback<ListViewConfiguration[]>
) {
  new RequestHandler().get(
    '/dashboards',
    { yesplanurl: host },
    (result: { body: ListViewConfiguration[] }, error: RequestError) =>
      callback(error ? null : listViewConfigurationsIn(result.body), error)
  );
}

export function fetchResourceOptions(
  host: string,
  callback: ResultCallback<{ resourceGroups: string[]; resourceRoles: string[] }>
) {
  new RequestHandler().get('/resourceoptions', { yesplanurl: host }, withBody(callback));
}

export function fetchEventOptions(
  host: string,
  callback: ResultCallback<{ eventStatuses: string[]; eventProfiles: string[] }>
) {
  new RequestHandler().get('/eventoptions', { yesplanurl: host }, withBody(callback));
}

export function fetchCustomDataFields(
  host: string,
  entity: 'resources' | 'resourcebookings',
  callback: ResultCallback<{ customDataTree: CustomDataTreeNode[] }>
) {
  new RequestHandler().get(
    `/customdatafields/${entity}`,
    { yesplanurl: host },
    withBody(callback)
  );
}

export function fetchViewSettings(
  host: string,
  viewID: string,
  callback: ResultCallback<StoredViewSettings>
) {
  new RequestHandler().get(
    `/viewsettings/${viewID}`,
    { yesplanurl: host },
    withBody(callback)
  );
}

/* Fire-and-forget: failing to save the user's last-used settings is not worth
   interrupting them for */
export function saveViewSettings(
  host: string,
  viewID: string,
  settings: StoredViewSettings
) {
  new RequestHandler().putWithRetry(
    `/viewsettings/${viewID}`,
    { yesplanurl: host },
    settings,
    ignoreResult
  );
}

export function fetchViewSnapshot(
  host: string,
  viewID: string,
  callback: ResultCallback<ListViewSnapshot>
) {
  new RequestHandler().get(
    `/viewsnapshot/${viewID}`,
    { yesplanurl: host },
    withBody(callback)
  );
}

/* Fire-and-forget, like the view settings. An oversized snapshot goes as a
   truncated stub: in-session markers keep working off the in-memory baseline,
   only markers across a page reload are lost then. */
export function saveViewSnapshot(
  host: string,
  viewID: string,
  snapshot: ListViewSnapshot
) {
  new RequestHandler().putWithRetry(
    `/viewsnapshot/${viewID}`,
    { yesplanurl: host },
    snapshotForStorage(snapshot),
    ignoreResult
  );
}

/* The route reads the day off the request headers, not the query string. The
   header is 'on', as on the other single-date routes: 'date' is a forbidden
   header name, and the browser drops it rather than sending it. */
export function fetchResourceBookings(
  host: string,
  date: ListViewDate,
  callback: ResultCallback<{ bookings: ListViewBooking[] }>
) {
  new RequestHandler().get(
    '/resourcebookings',
    { yesplanurl: host, on: serializeYesplanAPIDate(date) },
    withBody(callback)
  );
}
